import React, { Fragment, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { usePopper } from 'react-popper';

import { Popover, PopoverButton, PopoverPanel, Transition } from '@headlessui/react';
import dayjs from 'dayjs';

import useUser from '@/utils/hooks/swr/useUser';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useUserRole from '@/utils/hooks/user/useUserRole';

import PropTypes from 'prop-types';

import userDateFormat from '@/lib/user/dateFormat';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import { Arrow } from '@/components/composite/Popover/CustomPopover';

const ContentWrapper = ({ update, ...restProps }) => {
    useEffect(() => {
        // Force update => placement problem
        if (typeof update === 'function') {
            update();
        }
    }, []);

    return <div className="relative" {...restProps} />;
};

const SocialMediaEventInfoPopover = ({
    children,
    toggleDeleteModal,
    toggleEditModal,
    event,
    userDateFormatSettings,
    notEditable
}) => {
    const [referenceElement, setReferenceElement] = useState();
    const [popperElement, setPopperElement] = useState();
    const { update, styles, attributes } = usePopper(referenceElement, popperElement, {
        placement: 'bottom-end',
        modifiers: [
            {
                name: 'offset',
                options: {
                    offset: [0, 10]
                }
            }
        ]
    });

    return (
        <Popover className="w-full">
            {({ open }) => (
                <>
                    <PopoverButton
                        ref={setReferenceElement}
                        className="w-full"
                        as="div"
                        data-testid="trigger-event">
                        {children}
                    </PopoverButton>
                    {ReactDOM.createPortal(
                        <div
                            ref={setPopperElement}
                            style={styles.popper}
                            {...attributes.popper}
                            className="z-20">
                            <Transition
                                show={open}
                                as={Fragment}
                                enter="ease-out duration-200"
                                enterFrom="opacity-0 translate-y-1"
                                enterTo="opacity-100 translate-y-0"
                                leave="ease-in duration-150"
                                leaveFrom="opacity-100 translate-y-0"
                                leaveTo="opacity-0 translate-y-1">
                                <PopoverPanel>
                                    <ContentWrapper update={update}>
                                        <Arrow
                                            position={attributes?.popper?.['data-popper-placement']}
                                            isBorder
                                        />
                                        <div
                                            className="w-96 space-y-4 rounded-md border border-icoGray-300 bg-white p-4 text-xs text-icoBlack dark:border-icoDarkMode-beton dark:bg-icoDarkMode-anthracite dark:text-white"
                                            data-testid="popover-content">
                                            <div className="text-xl">{event.title}</div>
                                            <div className="flex flex-wrap text-icoGray-500 dark:text-icoDarkMode-wolf">
                                                {event.hashtags.map((hashtag, index) => {
                                                    return (
                                                        <span key={index} className="mr-1">
                                                            {hashtag}
                                                        </span>
                                                    );
                                                })}
                                            </div>
                                            <div className="flex items-center space-x-3">
                                                <div className="flex items-center">
                                                    <Icon
                                                        icon="calendar"
                                                        className="mr-2 text-icoGray-500 dark:text-icoDarkMode-wolf"
                                                    />
                                                    {dayjs(event.start_date).format(
                                                        userDateFormat[userDateFormatSettings] ??
                                                            'DD/MM/YYYY'
                                                    )}
                                                </div>
                                                {event.start_date !== event.end_date ? (
                                                    <>
                                                        <span className="my-1 h-px flex-1 bg-icoGray-300" />
                                                        <div className="flex items-center">
                                                            <Icon
                                                                icon="calendar"
                                                                className="mr-2 text-icoGray-500 dark:text-icoDarkMode-wolf"
                                                            />
                                                            {dayjs(event.end_date).format(
                                                                userDateFormat[
                                                                    userDateFormatSettings
                                                                ] ?? 'DD/MM/YYYY'
                                                            )}
                                                        </div>
                                                    </>
                                                ) : null}
                                            </div>
                                            <EditDeleteActions
                                                notEditable={notEditable}
                                                event={event}
                                                toggleDeleteModal={toggleDeleteModal}
                                                toggleEditModal={toggleEditModal}
                                            />
                                        </div>
                                    </ContentWrapper>
                                </PopoverPanel>
                            </Transition>
                        </div>,
                        document?.querySelector('.fc-popover') ?? document.body
                    )}
                </>
            )}
        </Popover>
    );
};

function EditDeleteActions({ notEditable, toggleDeleteModal, toggleEditModal, event }) {
    if (notEditable) {
        return null;
    }

    return (
        <EditDeleteContent
            toggleDeleteModal={toggleDeleteModal}
            toggleEditModal={toggleEditModal}
            event={event}
        />
    );
}

EditDeleteActions.propTypes = {
    notEditable: PropTypes.bool,
    toggleDeleteModal: PropTypes.func,
    toggleEditModal: PropTypes.func,
    event: PropTypes.object
};

function EditDeleteContent({ toggleDeleteModal, toggleEditModal, event }) {
    const { t } = useTranslation();
    const { user } = useUser();
    const { isAdmin } = useUserRole();

    const userCanUpdateEvent = isAdmin || user?.id === event?.user_id;

    if (!userCanUpdateEvent) {
        return (
            <p
                className="font-GMedium text-xs italic text-icoGray-500 dark:text-icoDarkMode-wolf"
                data-testid="notice-no-rights">
                {t('scheduler:custom_events.notice_not_owner_of_event')}
            </p>
        );
    }

    return (
        <div className="flex items-center justify-between" data-testid="edit-section">
            <button
                className="font-GMedium text-xs text-icoGray-500 underline dark:text-icoDarkMode-wolf"
                onClick={toggleDeleteModal}>
                {t('commons:delete')}
            </button>
            <Button size="sm" onClick={toggleEditModal}>
                {t('commons:edit')}
            </Button>
        </div>
    );
}

EditDeleteContent.propTypes = {
    toggleDeleteModal: PropTypes.func,
    toggleEditModal: PropTypes.func,
    event: PropTypes.object
};

SocialMediaEventInfoPopover.propTypes = {
    children: PropTypes.node,
    toggleDeleteModal: PropTypes.func,
    toggleEditModal: PropTypes.func,
    event: PropTypes.object
};

export default SocialMediaEventInfoPopover;
